import { FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUITextField } from '../../atomic/atoms';
import {
    SCUIBreadCrumb,
    SCUITextFieldWithSelectBox,
    SCUITextFieldWithButton,
    SCUICustomDataGrid,
    SCUISnackbar,
    SCUIYDMPRefundDialog,
} from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { GetYdMpOrderQuery } from '../../services/query';
import { enumSnackbarStatusType } from '../../types/enums';
import ClearIcon from '@mui/icons-material/Clear';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';

export const YdRefund: FC<any> = () => {
    const [showData, setShowData] = useState(false);
    const [snackbarShow, setSnackbarShow] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [snackbarStatus, setSnackbarStatus] = useState<enumSnackbarStatusType>(enumSnackbarStatusType.warning);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [barcodeAutoFocus, setbarcodeAutoFocus] = useState(false);
    const [orderNoAutoFocus, setOrderNoAutoFocus] = useState(false);
    const [order, setOrderData] = useState<any>(null);
    const [documentType, setDocumentType] = useState(1);
    const [mporderno, setMpOrderNo] = useState('');
    const [selectedRefundQuantities, setSelectedRefundQuantities] = useState<RefundQuantities>({});
    const [disabledButton, setDisabledButton] = useState(false);
    const [originCodeList, setOriginCodeList] = useState<{ id: string; value: string }[]>([]);
    const [selectedOriginCode, setSelectedOriginCode] = useState<any>(null);
    const [sendedMpOrderNoOrPoNumber, setSendedOrderOrPo] = useState<any>(null);
    const [lookupReturnShipmentNumber, setLookupReturnShipmentNumber] = useState<string | null>(null);
    const [invoiceNumber, setInvoiceNumber] = useState<any>(null);
    const { t } = useTranslation();
    const isAnyRefundSelected = Object.values(selectedRefundQuantities).some((quantity) => quantity > 0);

    enum OriginCode {
        Trendyol = 2323009,
        Kaspi = 2022028,
        NoonEg = 9945056,
    }

    const SearchCriteria = {
        MarketplaceOrderId: 1,
        ReturnShipmentNumber: 2,
    };

    useEffect(() => {
        getMpOrigins(false);
    }, []);

    useEffect(() => {
        const env = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;
        const defaultCode = getDefaultOriginCode(env);
        setSelectedOriginCode(defaultCode);
        setDocumentType(
            Number(defaultCode) === OriginCode.NoonEg
                ? SearchCriteria.ReturnShipmentNumber
                : SearchCriteria.MarketplaceOrderId,
        );
    }, [originCodeList]);

    const getDefaultOriginCode = (env: string | undefined) => {
        if (env === '10') {
            return originCodeList.find((origin) => origin.value === 'Kaspi-Kazakistan')?.id || null; // Örnek
        }
        if (env === '1') {
            return originCodeList.find((origin) => origin.value === 'Trendyol-Almanya')?.id || null; // Örnek
        }
        // Diğer env durumları
        return null;
    };
    const orderNoInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (orderNoAutoFocus && orderNoInputRef.current) {
            orderNoInputRef.current.focus();
        }
    }, [orderNoAutoFocus]);

    interface RefundQuantities {
        [barcode: string]: number;
    }

    function formatOrderStatus(status: any) {
        return status
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .toLowerCase()
            .replace(/^./, function (str: any) {
                return str.toUpperCase();
            });
    }

    function clearAllData() {
        setShowData(false);
        setOrderData(null);
        setMpOrderNo('');
        setSnackbarShow(false);
        setSnackbarMessage('');
        setOrderNoAutoFocus(true);
        setbarcodeAutoFocus(false);
        setSelectedRefundQuantities({});
        setSendedOrderOrPo(null);
        setLookupReturnShipmentNumber(null);
        setInvoiceNumber(null);
    }
    function onClose() {
        setOpenDialog(false);
        setDisabledButton(false);
    }
    const confirmReturn = async () => {
        setDisabledButton(true);
        let tmpReturns: { barcode: string; quantity: number }[] = [];

        Object.entries(selectedRefundQuantities).map(([barcode, quantity]) =>
            tmpReturns.push({
                barcode: barcode,
                quantity: quantity,
            })
        );

        let returnBody = {
            originCode: order.originCode,
            marketplaceOrderId:
                selectedOriginCode == OriginCode.Trendyol ? sendedMpOrderNoOrPoNumber : order.marketplaceOrderId,
            returnDate: new Date(),
            returnItems: tmpReturns,
            returnInvoiceNo: invoiceNumber,
            ...(lookupReturnShipmentNumber
                ? { returnShipmentNumber: lookupReturnShipmentNumber }
                : {}),
        };

        GetYdMpOrderQuery.ReturnYdMpOrder(returnBody)
            .then((res: any) => {
                console.log(res);
                if (!res.isSuccess) {
                    setSnackbarShow(true);
                    setSnackbarStatus(enumSnackbarStatusType.danger);
                    setSnackbarMessage(res.messages);
                } else {
                    onClose();
                    setShowData(false);
                    setDisabledButton(false);
                    setOrderData(null);
                    setMpOrderNo('');
                    setOrderNoAutoFocus(true);
                    setbarcodeAutoFocus(false);
                    setSelectedRefundQuantities({});
                    setSnackbarShow(true);
                    setSnackbarStatus(enumSnackbarStatusType.success);
                    setSnackbarMessage(t('YdMp.successReturn'));
                }
            })
            .catch(() => {
                console.log('Hata :>> ', t('YdMp.unknownError'));
            })
            .finally(() => {});
    };

    function getRefundedBarcodeQuantity(barcode: any) {
        return order.returnItems.filter((x: { barcode: any }) => x.barcode == barcode);
    }
    function getrefundableItems(barcode: any, saleQuantity: any) {
        let returnQuantity = order.returnItems.filter((x: { barcode: any }) => x.barcode == barcode)?.length;
        return saleQuantity - returnQuantity;
    }
    function getrefundableQuantity(barcode: any, saleQuantity: any) {
        let returnQuantity = order.returnItems.filter((x: { barcode: any }) => x.barcode == barcode)?.length;
        let selectedQuantity = selectedRefundQuantities[barcode] ?? 0;
        return saleQuantity - (returnQuantity + selectedQuantity);
    }

    function readBarcodeOnchange(barcode: string, saleQuantity: any) {
        let existBarcode = order.orderItems.find((x: { barcode: any }) => x.barcode == barcode);
        if (existBarcode == undefined || existBarcode == null) {
            setSnackbarShow(true);
            setSnackbarStatus(enumSnackbarStatusType.warning);
            setSnackbarMessage(t('YdMp.noFoundBarcode'));
        } else {
            setSelectedRefundQuantities((prevQuantities) => {
                const maxRefundableQuantity = getrefundableQuantity(barcode, saleQuantity);
                const currentQuantity = prevQuantities[barcode] || 0;
                if (maxRefundableQuantity < 1) {
                    setSnackbarShow(true);
                    setSnackbarStatus(enumSnackbarStatusType.warning);
                    setSnackbarMessage(t('YdMp.maxSelectRefundBarcode'));
                    return prevQuantities;
                }
                return {
                    ...prevQuantities,
                    [barcode]: currentQuantity + 1,
                };
            });
        }
    }
    const validateInvoiceNumber = (invoiceNumber: string | null): string => {
        if (!invoiceNumber) {
            return 'null'; // null veya boşsa "null" döner
        }
        const regex = /^[A-Za-z]{3}\d{13}$/;
        if (!regex.test(invoiceNumber) || invoiceNumber.length !== 16) {
            return 'invalidRegex'; // Regex kontrolü veya uzunluk hatası
        }
        return 'valid'; // Geçerli bir değer
    };

    const columns: ExtendedGridColDef[] = [
        { field: 'id', headerName: 'ID ', width: 250, hide: true },
        {
            field: 'barcode',
            headerName: t('YdMp.barcode'),
            editable: false,
            flex: 1,
        },
        {
            field: 'title',
            headerName: t('YdMp.productName'),
            editable: false,
            flex: 1,
        },
        {
            field: 'quantity',
            headerName: t('YdMp.saleQuantity'),
            editable: false,
            flex: 1,
        },
        {
            field: '2',
            headerName: t('YdMp.returnQuantity'),
            editable: false,
            flex: 1,
            valueGetter: (_params: any, row: any) => selectedRefundQuantities[row?.barcode] || 0,
            renderCell: (params: any) => (
                <Select
                    style={{ width: 75 }}
                    id={`refund-select-${params.row.barcode}`}
                    value={selectedRefundQuantities[params.row.barcode] || 0}
                    label="refund"
                    renderValue={() => selectedRefundQuantities[params.row.barcode] || 0}
                    onChange={(event) => {
                        const newQuantity = event.target.value;
                        setSelectedRefundQuantities((prev) => ({
                            ...prev,
                            [params.row.barcode]: newQuantity,
                        }));
                    }}
                >
                    {Array.from({ length: getrefundableItems(params.row.barcode, params.row.quantity) }, (_, i) => (
                        <MenuItem key={i} value={i + 1}>
                            {i + 1}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            field: 'taxableAmount',
            headerName: t('YdMp.productPrice'),
            editable: false,
            flex: 1,
        },
        {
            field: '1',
            headerName: t('YdMp.returnHistory'),
            editable: false,
            flex: 1,
            renderCell: (params: any) => {
                return getRefundedBarcodeQuantity(params.row.barcode).length;
            },
        },
        {
            field: 'clear',
            headerName: t('YdMp.clear'),
            editable: false,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton
                    onClick={() => {
                        setSelectedRefundQuantities((prev) => {
                            const updatedQuantities = { ...prev };
                            delete updatedQuantities[params.row.barcode];
                            return updatedQuantities;
                        });
                    }}
                    size="large"
                >
                    <ClearIcon />
                </IconButton>
            ),
            sortable: false,
            filterable: false,
            width: 100,
        },
    ];

    const getOrder = async (props: any) => {
        if (selectedOriginCode == null) {
            setSnackbarShow(true);
            setSnackbarStatus(enumSnackbarStatusType.danger);
            setSnackbarMessage(t('YdMp.originCodeError'));
        } else {
            clearAllData();
            const isReturnShipmentSearch =
                Number(selectedOriginCode) === OriginCode.NoonEg &&
                Number(documentType) === SearchCriteria.ReturnShipmentNumber;

            GetYdMpOrderQuery.GetYdMpOrder({
                OriginCode: selectedOriginCode,
                ...(isReturnShipmentSearch
                    ? { ReturnShipmentNumber: props }
                    : { MarketplaceOrderId: props }),
            })
                .then((res: any) => {
                    if (res) {
                        if (res.data.isSuccess == false) {
                            setSnackbarShow(true);
                            setSnackbarStatus(enumSnackbarStatusType.danger);
                            setSnackbarMessage(t(res.data.message));
                        } else {
                            setOrderData(res.data.orders[0].order);
                            setbarcodeAutoFocus(true);
                            setOrderNoAutoFocus(false);
                            setShowData(true);
                            setSendedOrderOrPo(
                                res.data.orders[0].order.marketplaceOrderId || props,
                            );
                            setLookupReturnShipmentNumber(
                                isReturnShipmentSearch ? String(props).trim() : null,
                            );
                        }
                    }
                })
                .catch((error: any) => {
                    console.log('Hata :>> ', error);
                })
                .finally(() => {});
        }
    };

    const getMpOrigins = async (props: any) => {
        GetYdMpOrderQuery.GetMpOrigins(props)
            .then((res: any) => {
                if (res) {
                    let orgTemp: { id: string; value: string }[] = [];
                    res.forEach((x: any) => {
                        let obj = { id: x.originCode, value: x.name };
                        orgTemp.push(obj);
                    });
                    setOriginCodeList(orgTemp);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => {});
    };

    return (
        <SCUIContent>
            <SCUIBreadCrumb title={t('YdMp.returnRequests')} componentLink={'ydmprefund'} />
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
                sx={{
                    margin: '1.5rem',
                    '& > .MuiGrid-item': {
                        paddingLeft: '16px !important',
                        paddingTop: '16px !important',
                    },
                }}
            >
                <Grid>
                    <FormControl style={{ width: 200 }}>
                        <InputLabel id="origin-code-label" shrink={!!selectedOriginCode}>
                            {t('YdMp.originCodes')}{' '}
                        </InputLabel>
                        <Select
                            labelId="origin-code-label"
                            value={selectedOriginCode}
                            label="Origin Code"
                            sx={{ height: '40px' }}
                            onChange={(event) => {
                                const nextOrigin = event.target.value;
                                setSelectedOriginCode(nextOrigin);
                                setDocumentType(
                                    Number(nextOrigin) === OriginCode.NoonEg
                                        ? SearchCriteria.ReturnShipmentNumber
                                        : SearchCriteria.MarketplaceOrderId,
                                );
                                setOrderNoAutoFocus(false);
                                setOrderNoAutoFocus(true);
                            }}
                        >
                            {originCodeList.map((origin) => (
                                <MenuItem key={origin.id} value={origin.id}>
                                    {origin.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid
                    justifyContent="flex-end"
                    textAlign="end"
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                        xl: 4,
                    }}
                >
                    <SCUITextFieldWithSelectBox
                        inputRef={orderNoInputRef}
                        key={`orderNo-${orderNoAutoFocus}-${selectedOriginCode}-${documentType}`}
                        selectList={
                            Number(selectedOriginCode) === OriginCode.NoonEg
                                ? [
                                      {
                                          value: SearchCriteria.ReturnShipmentNumber,
                                          text: t('YdMp.returnShipmentNumber'),
                                      },
                                      {
                                          value: SearchCriteria.MarketplaceOrderId,
                                          text: t('YdMp.mpOrderNo'),
                                      },
                                  ]
                                : [
                                      {
                                          value: SearchCriteria.MarketplaceOrderId,
                                          text: t(
                                              selectedOriginCode == OriginCode.Trendyol
                                                  ? 'YdMp.PoNumber'
                                                  : 'YdMp.mpOrderNo',
                                          ),
                                      },
                                  ]
                        }
                        onChangeSelect={(event: any) => {
                            setDocumentType(event.target.value as number);
                        }}
                        selectValue={documentType}
                        onChangeTextField={(event: any) => {
                            setMpOrderNo(event.target.value.trim().replace(/\s/g, ''));
                        }}
                        autoFocus={orderNoAutoFocus}
                        onKeyPressTextField={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                getOrder(mporderno);
                                e.target.value = '';
                            }
                        }}
                        onClickButton={() => {
                            getOrder(mporderno);
                            if (orderNoInputRef.current) {
                                orderNoInputRef.current.value = '';
                            }
                        }}
                    />
                </Grid>
            </Grid>
            <>
                {showData ? (
                    <Paper style={{ width: '100%', height: '20rem', padding: 1 }}>
                        <Grid container spacing={2} sx={{ width: '100%', margin: '1.5rem', marginTop: '6rem' }}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                }}
                            >
                                <p>
                                    <b>{t('YdMp.orderNo')}&nbsp;:&nbsp;</b>
                                    {order.orderNumber}
                                </p>
                                <p>
                                    <b>{t('YdMp.mpOrderNoLong')}&nbsp;:&nbsp;</b>
                                    {order.marketplaceOrderId}
                                </p>
                                <p>
                                    <b>{t('YdMp.mpOrderStatus')}&nbsp;:&nbsp;</b>
                                    {formatOrderStatus(order.orderStatus)}
                                </p>
                                {selectedOriginCode == OriginCode.Trendyol ? (
                                    <p>
                                        <b>{t('YdMp.PoNumber')}&nbsp;:&nbsp;</b>
                                        {sendedMpOrderNoOrPoNumber}
                                    </p>
                                ) : null}
                            </Grid>

                            {selectedOriginCode == OriginCode.Trendyol && (
                                <Grid
                                    container
                                    alignItems="center"
                                    size={{
                                        xs: 12,
                                        md: 6,
                                    }}
                                >
                                    <Grid
                                        container
                                        justifyContent="center"
                                        alignItems="center"
                                        size={{
                                            xs: 12,
                                            md: 6,
                                        }}
                                    >
                                        {/* <p style={{ whiteSpace: 'nowrap' }}><b>Fatura Numarası&nbsp;:&nbsp;</b></p> */}
                                        <SCUITextField
                                            stateName={''}
                                            label={t('YdMp.invoiceNumber')}
                                            shrink={true}
                                            variant={'outlined'}
                                            size="l"
                                            helperText={`${t('YdMp.ex')}: EEE2024000000612`}
                                            onChange={(e: any) => {
                                                setInvoiceNumber(e);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                ) : null}
            </>
            <>
                {showData ? (
                    <Paper
                        style={{
                            width: '100%',
                            height: '30rem',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 1,
                        }}
                    >
                        <Grid container direction="row" spacing={2} sx={{ margin: '1.5rem' }}>
                            <h4>
                                {order.orderNumber} {t('YdMp.productOfOrder')}
                            </h4>
                        </Grid>

                        <Grid container direction="row" spacing={2} sx={{ margin: '1.5rem' }}>
                            <SCUITextFieldWithButton
                                width={300}
                                autoFocus={barcodeAutoFocus}
                                placeholder={t('YdMp.barcodeNo')}
                                onKeyPress={(e: any) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        readBarcodeOnchange(
                                            e.target.value,
                                            order.orderItems.find((x: { barcode: any }) => x.barcode == e.target.value)
                                                ?.quantity
                                        );
                                        e.target.value = '';
                                    }
                                }}
                            />
                        </Grid>
                        <Grid
                            size={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                            }}
                        >
                            <SCUICustomDataGrid
                                randomId
                                rows={order.orderItems}
                                columns={columns}
                                hideFooter={false}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: '2', sort: 'desc' }],
                                    },
                                }}
                            />
                        </Grid>
                        <Grid
                            justifyContent="flex-end"
                            textAlign="end"
                            marginTop="1rem"
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 1.5,
                                xl: 2,
                            }}
                        >
                            <SCUIButton
                                text={t('YdMp.toReturn')}
                                type="secondary"
                                sizeType="fixed"
                                size={'m'}
                                onClick={() => {
                                    if (selectedOriginCode === OriginCode.Trendyol) {
                                        const validationResult = validateInvoiceNumber(invoiceNumber);

                                        if (validationResult === 'null') {
                                            setSnackbarShow(true);
                                            setSnackbarStatus(enumSnackbarStatusType.warning);
                                            setSnackbarMessage(t('YdMp.invoiceNumberError'));
                                        } else if (validationResult === 'invalidRegex') {
                                            setSnackbarShow(true);
                                            setSnackbarStatus(enumSnackbarStatusType.warning);
                                            setSnackbarMessage(t('YdMp.invoiceNumberFormatError'));
                                        } else {
                                            setOpenDialog(true);
                                        }
                                    } else {
                                        setOpenDialog(true);
                                    }
                                }}
                                disabled={!isAnyRefundSelected}
                            />
                            <SCUIButton
                                text={t('YdMp.clear')}
                                type="secondary"
                                sizeType="fixed"
                                size={'m'}
                                style={{ marginLeft: '1rem' }}
                                onClick={() => {
                                    clearAllData();
                                    setSelectedOriginCode(null);
                                }}
                            />
                        </Grid>
                    </Paper>
                ) : null}
            </>
            <SCUISnackbar
                show={snackbarShow}
                messageText={snackbarMessage}
                status={snackbarStatus}
                autoHideDuration={5000}
                onClose={() => {
                    setSnackbarShow(false);
                    setSnackbarStatus(enumSnackbarStatusType.success);
                    setSnackbarMessage('');
                }}
            />
            <SCUIYDMPRefundDialog
                open={openDialog}
                data={selectedRefundQuantities}
                onClose={onClose}
                order={order}
                confirmReturn={confirmReturn}
                disabledButton={disabledButton}
            />
        </SCUIContent>
    );
};
